import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest } from "../../services/api";
import { FiRefreshCw, FiSearch, FiLink } from "react-icons/fi";
import EditEmployeeMapping from './EditEmployeeMapping';
import MappingDialog from './MappingDialog';

const dateLabel = (value) =>
  value
    ? new Intl.DateTimeFormat("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      }).format(new Date(value.slice(0, 10) + "T00:00:00Z"))
    : "No end date";
const timeLabel = (value) =>
  new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Asia/Kolkata",
  }).format(new Date(value)) + " IST";
const todayIndia = () => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  return ["year", "month", "day"]
    .map((k) => parts.find((p) => p.type === k).value)
    .join("-");
};
export const mappingStatus = (row, today = todayIndia()) =>
  row.validFrom.slice(0, 10) > today
    ? "Scheduled"
    : row.validTo && row.validTo.slice(0, 10) < today
      ? "Ended"
      : "Current";
const inputClass =
  "mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-red-800 focus:ring-2 focus:ring-red-100";

export default function DatedMappingHistory({
  deviceId,
  accessToken,
  refresh = 0,
}) {
  const [rows, setRows] = useState([]),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(false);
  const [ending, setEnding] = useState(null),
    [date, setDate] = useState(""),
    [reason, setReason] = useState(""),
    [verified, setVerified] = useState(false),
    [busy, setBusy] = useState(false);
  const generation = useRef(0);
  const [editing,setEditing]=useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const scope=`${deviceId}:${accessToken}`;
  const currentScope=useRef(scope);currentScope.current=scope;
  useEffect(()=>{setEditing(null);setEnding(null);setBusy(false);},[deviceId,accessToken]);
  const [query, setQuery] = useState(""),
    [filter, setFilter] = useState("All");
  useEffect(() => { setPage(1); }, [query, filter, deviceId, pageSize]);
  const load = useCallback(async () => {
    const g = ++generation.current;
    setLoading(true);
    setError("");
    try {
      const result = await apiRequest(
        `/biometric-devices/${deviceId}/dated-mappings`,
        { accessToken },
      );
      if (g === generation.current) setRows(result);
    } catch (e) {
      if (g === generation.current)
        setError(e.message || "Unable to load dated mappings");
    } finally {
      if (g === generation.current) setLoading(false);
    }
  }, [deviceId, accessToken]);
  useEffect(() => {
    const requestGeneration = generation;
    load();
    return () => {
      requestGeneration.current++;
    };
  }, [load, refresh]);
  const end = async (e) => {
    e.preventDefault();
    if (!ending || !date || !verified || reason.trim().length < 10) return;
    setBusy(true);
    setError("");
    try {
      await apiRequest(
        `/biometric-devices/${deviceId}/dated-mappings/${ending.id}/end`,
        {
          method: "POST",
          accessToken,
          body: {
            validTo: date,
            reason: reason.trim(),
            identityVerified: true,
          },
        },
      );
      setEnding(null);
      await load();
    } catch (e) {
      setError(e.message || "Unable to end mapping");
    } finally {
      setBusy(false);
    }
  };
  const today = todayIndia();
  const visible = rows.filter(
    (row) =>
      (filter === "All" || mappingStatus(row, today) === filter) &&
      `${row.deviceUserId} ${row.employee?.employeeCode || ""} ${row.employee?.fullName || ""}`
        .toLowerCase()
        .includes(query.trim().toLowerCase()),
  );
  const totalPages = Math.max(1, Math.ceil(visible.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = visible.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-5 shadow-sm"
      aria-label="Employee mapping history"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="rounded-lg bg-red-50 p-2 text-red-800">
              <FiLink aria-hidden="true" />
            </span>
            <h3 className="text-lg font-bold text-slate-900">
              Employee mappings
            </h3>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
              {rows.length}
            </span>
          </div>
          <p className="mt-2 text-sm text-slate-500">
            Manage who owns each machine ID, and when.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={busy || loading}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
        >
          <FiRefreshCw
            aria-hidden="true"
            className={loading ? "animate-spin" : ""}
          />
          Refresh history
        </button>
      </div>
      <div className="rounded-xl bg-slate-50 px-4 py-3 text-xs leading-5 text-slate-600">
        <span className="font-semibold text-slate-800">
          Dates include the first and last day · India time.
        </span>{" "}
        End the former assignment before reusing an ID. Mapping changes do not
        deactivate employees or change attendance/payroll.
      </div>
      {error && (
        <p
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800"
        >
          {error}
        </p>
      )}
      {!!rows.length && (
        <div className="space-y-3">
          <div className="relative">
            <FiSearch
              aria-hidden="true"
              className="absolute left-3 top-3 text-slate-400"
            />
            <input
              aria-label="Search mapping history"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search employee, code or machine ID"
              className="w-full rounded-xl border border-slate-200 py-2.5 pl-10 pr-3 text-sm outline-none focus:border-red-800 focus:ring-2 focus:ring-red-100"
            />
          </div>
          <div className="flex flex-wrap gap-2" aria-label="Filter mappings">
            {["All", "Current", "Scheduled", "Ended"].map((label) => (
              <button
                key={label}
                type="button"
                aria-pressed={filter === label}
                onClick={() => setFilter(label)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${filter === label ? "bg-red-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              >
                {label}{" "}
                <span className="ml-1 opacity-70">
                  {label === "All"
                    ? rows.length
                    : rows.filter((r) => mappingStatus(r, today) === label)
                        .length}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
      {loading ? (
        <p role="status" className="py-10 text-center text-sm text-slate-500">
          Loading dated mappings…
        </p>
      ) : !rows.length ? (
        <div className="rounded-xl border border-dashed border-slate-200 px-5 py-10 text-center">
          <FiLink
            aria-hidden="true"
            className="mx-auto mb-3 text-slate-300"
            size={26}
          />
          <p className="font-semibold text-slate-800">
            No dated mappings. HR must verify and add them using the form above.
          </p>
        </div>
      ) : !visible.length ? (
        <p className="rounded-xl bg-slate-50 p-6 text-center text-sm text-slate-500">
          No mappings match this search or filter.
        </p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-left text-sm">
          <caption className="sr-only">Employee mappings with inclusive validity dates and review history</caption>
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr>{['Machine ID', 'Employee', 'Assignment', 'Valid dates', 'Actions & history'].map(label => <th key={label} scope="col" className="px-4 py-3 font-semibold">{label}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
          {pageRows.map((row) => {
            const status = mappingStatus(row, today);
            return (
              <tr
                key={row.id}
                className="align-top hover:bg-slate-50/60"
              >
                  <td className="px-4 py-4">
                      <span className="inline-block rounded-lg bg-red-50 px-3 py-1 font-mono font-semibold text-red-900">
                        {row.deviceUserId}
                      </span>
                  </td>
                    <td className="px-4 py-4">
                      <h4 className="break-words font-semibold text-slate-900">
                        {row.employee?.fullName || "Employee unavailable"}
                      </h4>
                      <p className="mt-1 text-xs text-slate-500">
                        {row.employee?.employeeCode || "No employee code"} ·
                        Employee:{" "}
                        {row.employee?.status?.toLowerCase() || "unknown"}
                      </p>
                    </td>
                    <td className="px-4 py-4"><span
                        className={`mt-2 inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${status === "Current" ? "bg-green-50 text-green-800" : status === "Scheduled" ? "bg-blue-50 text-blue-800" : "bg-slate-100 text-slate-600"}`}
                      >
                        {status} assignment
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-4"><p className="font-medium text-slate-800">{dateLabel(row.validFrom)}</p><p className="mt-1 text-xs text-slate-500">to <span>{dateLabel(row.validTo)}</span></p></td>
                  <td className="max-w-sm px-4 py-4"><div className="flex flex-wrap items-center gap-2">
                  {!row.closedAt&&<button type="button" disabled={busy||row.canEdit===false} title={row.editBlockedReason||''} onClick={()=>{setEnding(null);setEditing(row);}} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">Edit mapping</button>}
                  {!row.validTo ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setEnding(row);
                        setEditing(null);
                        setDate("");
                        setReason("");
                        setVerified(false);
                      }}
                      className="rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-800 hover:bg-red-50 disabled:opacity-50"
                    >
                      End mapping
                    </button>
                  ) : (
                    <span className="text-xs text-slate-400">
                      Retained history
                    </span>
                  )}
                  </div>
                {row.editBlockedReason&&!row.closedAt&&<p className="mt-3 text-xs text-amber-800">{row.editBlockedReason}</p>}
                <details className="mt-4 text-sm">
                  <summary className="cursor-pointer text-xs font-semibold text-slate-600 hover:text-red-800">
                    Review details &amp; audit history
                  </summary>
                  <div className="mt-3 space-y-3 border-t border-slate-100 pt-3 text-xs leading-5 text-slate-600">
                    <div>
                      <p className="font-semibold text-slate-800">
                        Review reason
                      </p>
                      <p className="whitespace-pre-wrap break-words">
                        {row.reason}
                      </p>
                    </div>
                    <div>
                      <p>Created {timeLabel(row.createdAt)}</p>
                      <p className="break-all text-slate-400">
                        Recorded by user ID: {row.createdBy}
                      </p>
                    </div>
                    {row.closedAt && (
                      <div>
                        <p className="font-semibold text-slate-800">
                          End-date review
                        </p>
                        <p className="whitespace-pre-wrap break-words">
                          {row.closeReason}
                        </p>
                        <p className="mt-1">Ended {timeLabel(row.closedAt)}</p>
                        <p className="break-all text-slate-400">
                          Recorded by user ID: {row.closedBy}
                        </p>
                      </div>
                    )}
                    {(row.editHistory||[]).map((edit,i)=><div key={edit.requestKey} className="border-t border-slate-100 pt-3"><p className="font-semibold text-slate-800">Mapping edit {i+1}</p><p>{edit.reason}</p><p>{timeLabel(edit.editedAt)} · Recorded by {edit.actorId}</p><p className="break-all">Before: employee {edit.before.employeeId} · machine ID {edit.before.deviceUserId} · {edit.before.validFrom} to {edit.before.validTo||'No end date'}</p><p className="break-all">After: employee {edit.after.employeeId} · machine ID {edit.after.deviceUserId} · {edit.after.validFrom} to {edit.after.validTo||'No end date'}</p></div>)}
                  </div>
                </details>
                  </td>
              </tr>
            );
          })}
          </tbody>
        </table>
        </div>
        <nav aria-label="Mapping pagination" className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-xs text-slate-600">Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, visible.length)} of {visible.length} mappings</p>
          <div className="flex items-center gap-3"><label className="text-xs text-slate-600">Rows per page <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} className="ml-2 rounded-lg border border-slate-200 bg-white p-2">{[10,25,50].map(size => <option key={size} value={size}>{size}</option>)}</select></label>
          <button type="button" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs disabled:opacity-40">Previous</button><span className="text-xs text-slate-600">Page {currentPage} of {totalPages}</span><button type="button" disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs disabled:opacity-40">Next</button></div>
        </nav>
        </div>
      )}
      {editing&&<MappingDialog title="Edit employee mapping" busy={busy} onClose={()=>setEditing(null)}><EditEmployeeMapping key={`${deviceId}:${editing.id}:${editing.payloadHash}`} row={editing} deviceId={deviceId} accessToken={accessToken} onBusyChange={value=>{if(currentScope.current===scope)setBusy(value);}} onCancel={()=>setEditing(null)} onSaved={()=>{if(currentScope.current===scope){setEditing(null);load();}}}/></MappingDialog>}
      {ending && (
        <MappingDialog title="End employee mapping" busy={busy} onClose={()=>setEnding(null)}>
        <form
          onSubmit={end}
          className="space-y-4 rounded-2xl border border-amber-200 bg-amber-50 p-5"
        >
          <h4 className="font-bold text-amber-950">End employee mapping</h4>
          <p className="text-sm leading-6 text-amber-900">
            End machine ID {ending.deviceUserId} for {ending.employee?.fullName}
            . Identity and start date cannot be overwritten.
          </p>
          <label className="block text-sm font-medium text-slate-800">
            Last valid date (inclusive)
            <input
              required
              type="date"
              min={ending.validFrom.slice(0, 10)}
              value={date}
              disabled={busy}
              onChange={(e) => setDate(e.target.value)}
              className={inputClass}
            />
          </label>
          <label className="block text-sm font-medium text-slate-800">
            Reason
            <textarea
              required
              minLength={10}
              maxLength={1000}
              value={reason}
              disabled={busy}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Explain why this assignment is ending"
              className={inputClass}
            />
          </label>
          <label className="flex items-start gap-3 text-sm leading-6 text-amber-950">
            <input
              type="checkbox"
              checked={verified}
              disabled={busy}
              className="mt-1 h-4 w-4 shrink-0 accent-red-800"
              onChange={(e) => setVerified(e.target.checked)}
            />{" "}
            I verified the employee and last valid date. Later punches will need
            a separate assignment.
          </label>
          <button
            disabled={busy || !date || !verified || reason.trim().length < 10}
            className="rounded-xl bg-red-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-900 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Confirm end date
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setEnding(null)}
            className="ml-3 rounded-xl border border-amber-300 px-4 py-2.5 text-sm font-medium text-amber-900 hover:bg-amber-100"
          >
            Cancel
          </button>
        </form>
        </MappingDialog>
      )}
    </section>
  );
}
